import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  SettingsOutlined,
  PsychologyOutlined,
  RecordVoiceOverOutlined,
  AltRouteOutlined,
  CodeOutlined,
  Close,
  SmartToyOutlined,
  AccountTreeOutlined,
  BuildOutlined,
  DevicesOutlined,
  QueryStatsOutlined,
} from "@mui/icons-material";

import QueueSelect from "../QueueSelect";
import ConnectionModalSidebar from "../ConnectionModalSidebar";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { REALTIME_MODELS, REALTIME_VOICES } from "../../helpers/realtimeVoices";
import { isPromptOfType, promptOptionLabel, promptsOfType } from "../../helpers/promptTypes";

const FORMA_RADIOS = {
  square: { burbuja: 4, pantalla: 8 },
  rounded: { burbuja: 16, pantalla: 24 },
  veryRounded: { burbuja: 32, pantalla: 44 },
};
const TAMANO_PX = {
  small: { w: 150, h: 190 },
  medium: { w: 190, h: 230 },
  large: { w: 230, h: 278 },
};

const mezclarConBlanco = (hex, pct) => {
  const limpio = String(hex || "").replace("#", "");
  if (limpio.length !== 6) return hex;
  const n = parseInt(limpio, 16);
  if (Number.isNaN(n)) return hex;
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  r = Math.round(r + (255 - r) * pct);
  g = Math.round(g + (255 - g) * pct);
  b = Math.round(b + (255 - b) * pct);
  return `rgb(${r},${g},${b})`;
};

const PESTANAS = [
  { clave: "general", etiqueta: "General", icono: <SettingsOutlined fontSize="small" /> },
  {
    clave: "inteligencia",
    etiqueta: "Inteligencia",
    icono: <PsychologyOutlined fontSize="small" />,
  },
  {
    clave: "avatar3d",
    etiqueta: "Voz y apariencia",
    icono: <RecordVoiceOverOutlined fontSize="small" />,
  },
  {
    clave: "vistaprevia",
    etiqueta: "Vista previa",
    icono: <DevicesOutlined fontSize="small" />,
  },
  {
    clave: "enrutamiento",
    etiqueta: "Enrutamiento",
    icono: <AltRouteOutlined fontSize="small" />,
  },
  { clave: "integracion", etiqueta: "Integración", icono: <CodeOutlined fontSize="small" /> },
  { clave: "consumo", etiqueta: "Consumo", icono: <QueryStatsOutlined fontSize="small" /> },
];

const ETIQUETA_RESULTADO = {
  hung_up: "Cerradas por el visitante",
  time_limit: "Cortadas por límite de tiempo",
  error: "Cortadas por un error",
  unknown: "Sin registrar",
};

const formatearMinutos = (segundos) => {
  const min = Math.floor(segundos / 60);
  const seg = segundos % 60;
  return min > 0 ? `${min} min ${seg} s` : `${seg} s`;
};

const TOOLS_DISPONIBLES = [
  { id: "crear_lead", etiqueta: "Crear lead", descripcion: "Registra nombre y teléfono para que un asesor contacte después." },
];

const Ajuste = ({ titulo, descripcion, control, classes }) => (
  <Box className={classes.ajuste}>
    <Box style={{ flex: 1, minWidth: 0 }}>
      <Typography className={classes.ajusteTitulo}>{titulo}</Typography>
      <Typography className={classes.ajusteDesc}>{descripcion}</Typography>
    </Box>
    {control}
  </Box>
);

const Grupo = ({ children, classes }) => (
  <Typography className={classes.grupo}>{children}</Typography>
);

const THREE_VERSION = "0.180.0";
const TALKINGHEAD_VERSION = "1.7";

let cargaTalkingHead = null;
function cargarTalkingHead() {
  if (cargaTalkingHead) return cargaTalkingHead;

  cargaTalkingHead = new Promise((resolve, reject) => {
    const yaHabia = Boolean(document.querySelector('script[type="importmap"]'));
    if (!yaHabia) {
      const mapa = document.createElement("script");
      mapa.type = "importmap";
      mapa.textContent = JSON.stringify({
        imports: {
          three: `https://cdn.jsdelivr.net/npm/three@${THREE_VERSION}/build/three.module.js`,
          "three/addons/": `https://cdn.jsdelivr.net/npm/three@${THREE_VERSION}/examples/jsm/`,
          talkinghead: `https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@${TALKINGHEAD_VERSION}/modules/talkinghead.mjs`,
        },
      });
      document.head.appendChild(mapa);
      console.log("[avatar3d] importmap inyectado:", mapa.textContent);
    }

    // eslint-disable-next-line no-eval
    (0, eval)('import("talkinghead")')
      .then((mod) => {
        console.log("[avatar3d] import(\"talkinghead\") resolvio");
        resolve(mod);
      })
      .catch((err) => {
        console.error('[avatar3d] fallo el import("talkinghead")', err);
        reject(err);
      });
  });

  return cargaTalkingHead;
}

const CAMERA_VIEWS = [
  { value: "full", label: "Cuerpo entero" },
  { value: "mid", label: "Medio cuerpo" },
  { value: "upper", label: "Torso" },
  { value: "head", label: "Primer plano" },
];

const Avatar3DCanvas = ({ url, body, cameraView, background, classes }) => {
  const contenedorRef = useRef(null);
  const [estado, setEstado] = useState("idle");
  const [detalleError, setDetalleError] = useState("");

  useEffect(() => {
    const contenedor = contenedorRef.current;
    if (!contenedor) return;

    contenedor.innerHTML = "";

    if (!url) {
      setEstado("idle");
      return;
    }

    let cancelado = false;
    setEstado("cargando");
    setDetalleError("");

    console.log("[avatar3d] arrancando carga de", url, "body:", body);

    cargarTalkingHead()
      .then((mod) => {
        console.log("[avatar3d] talkinghead.mjs cargado, exports:", Object.keys(mod));
        if (cancelado) return null;

        const head = new mod.TalkingHead(contenedor, { cameraView: cameraView || "mid" });
        console.log("[avatar3d] instancia de TalkingHead creada, pidiendo showAvatar()...");

        return head.showAvatar({ url, body: body === "M" ? "M" : "F" });
      })
      .then((resultado) => {
        console.log("[avatar3d] showAvatar() resolvio bien:", resultado);
        if (!cancelado) setEstado("listo");
      })
      .catch((err) => {
        console.error("[avatar3d] fallo la carga:", err);
        if (cancelado) return;
        setEstado("error");
        setDetalleError(String(err?.message || err));
      });

    return () => {
      cancelado = true;
    };
  }, [url, body, cameraView]);

  return (
    <>
      {}
      {background && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            background: `url("${background}") center / cover no-repeat`,
          }}
        />
      )}
      <div
        ref={contenedorRef}
        style={{ width: "100%", height: "100%", position: "relative", zIndex: 1 }}
      />

      {estado !== "listo" && (
        <Box
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            pointerEvents: "none",
            padding: 12,
            boxSizing: "border-box",
          }}
        >
          {estado === "idle" && (
            <Typography className={classes.ayuda} style={{ textAlign: "center", fontSize: 11 }}>
              Elegí o pegá una URL de modelo arriba para verlo acá.
            </Typography>
          )}
          {estado === "cargando" && (
            <Typography className={classes.ayuda} style={{ textAlign: "center", fontSize: 11 }}>
              Cargando…
            </Typography>
          )}
          {estado === "error" && (
            <>
              <Typography style={{ fontSize: 22 }}>⚠️</Typography>
              <Typography
                style={{ color: "#e5484d", textAlign: "center", fontSize: 11.5, fontWeight: 600 }}
              >
                Este modelo no cargó
              </Typography>
              <Typography className={classes.ayuda} style={{ textAlign: "center", fontSize: 10.5 }}>
                {detalleError}
              </Typography>
            </>
          )}
        </Box>
      )}
    </>
  );
};

const Avatar3DPreview = ({ url, body, cameraView, background, color, classes }) => (
  <Box className={classes.previaCol}>
    <Typography className={classes.previaRotulo}>Vista previa (modelo real)</Typography>

    <Box
      className={classes.previa3D}
      style={{
        position: "relative",
        background: `radial-gradient(circle at 50% 35%, ${mezclarConBlanco(color || "#3956ff", 0.95)} 0%, ${mezclarConBlanco(color || "#3956ff", 0.85)} 55%, ${mezclarConBlanco(color || "#3956ff", 0.72)} 100%)`,
      }}
    >
      <Avatar3DCanvas url={url} body={body} cameraView={cameraView} background={background} classes={classes} />
    </Box>
  </Box>
);

const useStyles = makeStyles()((theme) => ({
  lineaArbol: {
    marginLeft: 11,
    marginTop: 8,
    paddingLeft: 17,
    borderLeft: `2px solid ${theme.palette.mode === "light" ? "#d8dbe3" : "rgba(255,255,255,0.14)"}`,
  },
  caja: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 1280,
    maxWidth: "96vw",
    height: 760,
    maxHeight: "94vh",
    display: "flex",
    flexDirection: "column",
    outline: "none",
    overflow: "hidden",
  },
  cabecera: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.25, 2.5, 2),
  },
  insignia: {
    width: 38,
    height: 38,
    borderRadius: 10,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(57,86,255,0.14)",
    color: "#5b6cff",
  },
  titulo: { fontSize: 16, fontWeight: 600, lineHeight: 1.2 },
  sub: { fontSize: 12.5, color: theme.palette.text.secondary },
  grupo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    "&::after": {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.divider,
    },
  },
  ajuste: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    background:
      theme.palette.mode === "light" ? "rgba(0,0,0,0.015)" : "rgba(255,255,255,0.022)",
  },
  ajusteTitulo: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 },
  ajusteDesc: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: 2,
  },
  cuerpo: { display: "flex", flex: 1, minHeight: 0 },
  panel: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: theme.spacing(2.5, 3, 3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.75),
  },
  ayuda: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 4 },
  codigo: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    background: theme.palette.mode === "light" ? "#f5f4ef" : "#1c1c1a",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    userSelect: "all",
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  previaCol: {
    flex: "0 0 300px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    borderLeft: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.mode === "light" ? "rgba(0,0,0,0.022)" : "rgba(255,255,255,0.028)",
    padding: theme.spacing(2.5, 2.5),
    overflowY: "auto",
  },
  previaRotulo: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    alignSelf: "flex-start",
  },
  previa3D: {
    width: "100%",
    height: 380,
    borderRadius: 12,
    background: "radial-gradient(circle at 50% 35%,#f5f6ff 0%,#dde3ff 55%,#c3cdfb 100%)",
    border: `1px dashed ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  pie: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.75, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const AvatarModal = ({ open, onClose, conexion, backendUrl }) => {
  const { classes } = useStyles();

  const [pestana, setPestana] = useState("general");
  const [guardando, setGuardando] = useState(false);
  const [token, setToken] = useState("");
  const [nombre, setNombre] = useState("");
  const [promptId, setPromptId] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [colas, setColas] = useState([]);
  const [config, setConfig] = useState({});
  const [origenNuevo, setOrigenNuevo] = useState("");
  const [realtimeModel, setRealtimeModel] = useState("gpt-realtime-2.1-mini");
  const [realtimeVoice, setRealtimeVoice] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data?.prompts || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const [uso, setUso] = useState(null);
  const [cargandoUso, setCargandoUso] = useState(false);

  useEffect(() => {
    if (!open || pestana !== "consumo" || !conexion?.id) return;

    (async () => {
      setCargandoUso(true);
      try {
        const { data } = await api.get(`/whatsapp/avatar/${conexion.id}/usage`);
        setUso(data);
      } catch (err) {
        toastError(err);
      } finally {
        setCargandoUso(false);
      }
    })();
  }, [open, pestana, conexion]);

  const [modelosDisponibles, setModelosDisponibles] = useState([]);
  const [availableBackgrounds, setAvailableBackgrounds] = useState([]);

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const { data } = await api.get("/whatsapp/avatar/modelos");
        setModelosDisponibles(data || []);
      } catch (err) {
        toastError(err);
      }

      try {
        const { data } = await api.get("/whatsapp/avatar/fondos");
        setAvailableBackgrounds(data || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !conexion) return;

    setPestana("general");
    setToken(conexion.token || "");
    setNombre(conexion.name || "");
    setPromptId(conexion.promptId || "");
    setColas((conexion.queues || []).map((q) => q.id));
    setConfig({
      allowedOrigins: [],
      avatarModelUrl: "",
      avatarBody: "F",
      avatarCameraView: "mid",
      avatarBackgroundUrl: "",
      toolIds: [],
      greeting: "¡Hola! ¿En qué te puedo ayudar?",
      maxSessionMinutes: 10,
      maxOutputTokens: 1200,
      habilitado: false,
      exposedToApp: false,
      color: "#3956ff",
      position: "right",
      size: "small",
      shape: "rounded",
      buttonText: "Comenzar",
      noticeText: "",
      avatarName: "Avatar",
      monthlyMinutesLimit: 0,
      askForDetails: "never",
      ...(conexion.avatarConfig || {}),
    });
  }, [open, conexion]);

  useEffect(() => {
    const elegido = prompts.find((p) => p.id === promptId);
    setRealtimeModel(elegido?.realtimeModel || "gpt-realtime-2.1-mini");
    setRealtimeVoice(elegido?.realtimeVoice || "");
  }, [promptId, prompts]);

  const snippet = useMemo(
    () => `<script src="${backendUrl}/public/avatar.js"\n        data-token="${token}"></script>`,
    [backendUrl, token]
  );

  const pruebaUrl = useMemo(() => `${backendUrl}/avatar/${token}/probar`, [backendUrl, token]);

  const cambiar = (clave) => (valor) => setConfig((p) => ({ ...p, [clave]: valor }));

  const agregarOrigen = () => {
    const limpio = origenNuevo.trim();
    if (!limpio) return;

    setConfig((p) => ({ ...p, allowedOrigins: [...(p.allowedOrigins || []), limpio] }));
    setOrigenNuevo("");
  };

  const alternarTool = (id) => {
    setConfig((p) => {
      const activos = p.toolIds || [];
      const yaEsta = activos.includes(id);
      return {
        ...p,
        toolIds: yaEsta ? activos.filter((x) => x !== id) : [...activos, id],
      };
    });
  };

  const guardar = async () => {
    if (guardando) return;
    setGuardando(true);

    const fallos = [];

    try {
      await api.put(`/whatsapp/avatar/${conexion.id}`, { config });
    } catch (err) {
      fallos.push("los ajustes del avatar");
      toastError(err);
    }

    try {
      await api.put(`/whatsapp/${conexion.id}`, {
        name: nombre,
        promptId: promptId || null,
        queueIds: colas,
      });
    } catch (err) {
      fallos.push("el nombre/agente");
      toastError(err);
    }

    if (promptId) {
      const promptCompleto = prompts.find((p) => p.id === promptId);

      if (promptCompleto) {
        try {
          await api.put(`/prompt/${promptId}`, {
            ...promptCompleto,
            realtimeModel,
            realtimeVoice,
          });
        } catch (err) {
          fallos.push("el modelo/voz de Realtime");
          toastError(err);
        }
      }
    }

    setGuardando(false);

    if (fallos.length) return;

    toast.success("Conexión actualizada.");
    onClose(true);
  };

  const rotarToken = async () => {
    try {
      const { data } = await api.put(`/whatsapp/avatar/${conexion.id}/token`);
      setToken(data.token);
      toast.warning("Token cambiado. Volvé a pegar el script en el sitio o el avatar dejará de funcionar.");
    } catch (err) {
      toastError(err);
    }
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Copiado.");
    } catch {}
  };

  if (!conexion) return null;

  return (
    <Modal open={open} onClose={() => onClose(false)} aria-labelledby="avatar-title">
      <Paper className={classes.caja}>
        <Box className={classes.cabecera}>
          <Box className={classes.insignia}>
            <RecordVoiceOverOutlined fontSize="small" />
          </Box>

          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.titulo} id="avatar-title">
              Editar avatar de voz
            </Typography>
            <Typography className={classes.sub}>{conexion.name}</Typography>
          </Box>

          <IconButton size="small" onClick={() => onClose(false)} aria-label="Cerrar">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box className={classes.cuerpo}>
          <ConnectionModalSidebar
            tabs={PESTANAS.map((p) => ({ value: p.clave, label: p.etiqueta, icon: p.icono }))}
            active={pestana}
            onChange={setPestana}
          />

          <Box className={classes.panel}>
            {pestana === "general" && (
              <>
                <Grupo classes={classes}>Identificación</Grupo>

                <TextField
                  label="Nombre"
                  size="small"
                  fullWidth
                  required
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />

                <Grupo classes={classes}>Estado</Grupo>

                <Ajuste
                  classes={classes}
                  titulo="Avatar habilitado"
                  descripcion="Apagado, /avatar/:token/sesion responde como si la conexión no existiera y el widget no arranca ninguna llamada."
                  control={
                    <Switch
                      checked={config.enabled === true}
                      onChange={(e) => cambiar("enabled")(e.target.checked)}
                    />
                  }
                />

                <Grupo classes={classes}>Conversación</Grupo>

                <TextField
                  label="Saludo inicial"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={config.greeting || ""}
                  onChange={(e) => cambiar("greeting")(e.target.value)}
                  helperText="Lo primero que dice el avatar al conectar."
                />

                <TextField
                  label="Minutos máximos por llamada"
                  size="small"
                  type="number"
                  value={config.maxSessionMinutes ?? 10}
                  onChange={(e) => cambiar("maxSessionMinutes")(Number(e.target.value))}
                  helperText="Corte duro de sesión, para controlar el costo de OpenAI Realtime."
                />

                <TextField
                  label="Tope de minutos por mes"
                  size="small"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={config.monthlyMinutesLimit ?? 0}
                  onChange={(e) => cambiar("monthlyMinutesLimit")(Number(e.target.value))}
                  helperText="Al superarlo, el avatar deja de aceptar llamadas nuevas hasta el mes siguiente. 0 = sin tope."
                />

                <TextField
                  label="Máximo de tokens por respuesta"
                  size="small"
                  type="number"
                  style={{ marginTop: 16 }}
                  inputProps={{ min: 500, max: 4096 }}
                  value={config.maxOutputTokens ?? 1200}
                  onChange={(e) => cambiar("maxOutputTokens")(Number(e.target.value))}
                  helperText={`≈ ${Math.round((config.maxOutputTokens ?? 1200) / 20)} segundos de habla antes de cortar la respuesta a la fuerza (20 tokens = 1 segundo). No es por palabras de texto. Mínimo 500 (≈25 s) para no cortar a mitad de frase, tope de OpenAI 4096 (≈205 s).`}
                />
              </>
            )}

            {pestana === "inteligencia" && (
              <>
                <Grupo classes={classes}>Inteligencia</Grupo>

                <FormControl size="small" fullWidth>
                  <InputLabel>Prompt (IA)</InputLabel>
                  <Select
                    label="Prompt (IA)"
                    value={promptId}
                    onChange={(e) => setPromptId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Sin asignar</em>
                    </MenuItem>
                    {promptsOfType(prompts, "voice", promptId).map((p) => (
                      <MenuItem key={p.id} value={p.id} disabled={!isPromptOfType(p, "voice")}>
                        {promptOptionLabel(p, "voice")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {promptId && (
                  <FormControl size="small" fullWidth style={{ marginTop: 16 }}>
                    <InputLabel>Modelo (OpenAI Realtime)</InputLabel>
                    <Select
                      label="Modelo (OpenAI Realtime)"
                      value={realtimeModel}
                      onChange={(e) => setRealtimeModel(e.target.value)}
                    >
                      {REALTIME_MODELS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, marginLeft: 2 }}>
                      La voz y el aspecto del avatar se eligen en la pestaña "Voz y apariencia".
                    </Typography>
                  </FormControl>
                )}

                {promptId && (() => {
                  const elegido = prompts.find((p) => p.id === promptId);
                  if (!elegido) return null;

                  const hijos = elegido.promptsHijos || [];
                  const herramientasPropias = elegido.herramientasWebhook?.length || 0;

                  const chipHerramientas = (n) => (
                    <Chip
                      size="small"
                      icon={<BuildOutlined style={{ fontSize: 14 }} />}
                      label={n > 0 ? `${n} herramienta${n === 1 ? "" : "s"}` : "Sin herramientas"}
                      color={n > 0 ? "primary" : "default"}
                      variant={n > 0 ? "filled" : "outlined"}
                    />
                  );

                  return (
                    <Paper variant="outlined" style={{ padding: 12, marginTop: 16 }}>
                      <Typography
                        className={classes.ayuda}
                        style={{
                          marginBottom: 10,
                          textTransform: "uppercase",
                          fontSize: 11,
                          letterSpacing: 0.4,
                          fontWeight: 700,
                        }}
                      >
                        Estructura de este Prompt
                      </Typography>

                      <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <SmartToyOutlined fontSize="small" color="primary" />
                        <Typography variant="body2" style={{ fontWeight: 700, flex: 1 }}>
                          {elegido.name}
                        </Typography>
                        {chipHerramientas(herramientasPropias)}
                      </Box>

                      {hijos.length === 0 ? (
                        <Typography className={classes.ayuda} style={{ marginTop: 8, marginLeft: 28 }}>
                          Sin especialistas vinculados.
                        </Typography>
                      ) : (
                        <Box className={classes.lineaArbol}>
                          {hijos.map((hijo) => {
                            const herramientasHijo = hijo.herramientasWebhook?.length || 0;
                            return (
                              <Box
                                key={hijo.id}
                                style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
                              >
                                <AccountTreeOutlined fontSize="small" color="action" />
                                <Typography variant="body2" style={{ flex: 1 }}>
                                  {hijo.name}
                                </Typography>
                                {chipHerramientas(herramientasHijo)}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Paper>
                  );
                })()}
              </>
            )}

            {pestana === "avatar3d" && (
              <>
                <Grupo classes={classes}>Modelo 3D</Grupo>

                <Typography className={classes.ayuda}>
                  {config.avatarModelUrl
                    ? "Configurado -- mirá cómo queda en la vista previa de la derecha."
                    : "Sin esto, la tarjeta del widget queda vacía -- no hay personaje de respaldo."}
                </Typography>

                {modelosDisponibles.length > 0 && (
                  <FormControl size="small" fullWidth>
                    {}
                    <InputLabel shrink>Modelo ya disponible</InputLabel>
                    <Select
                      label="Modelo ya disponible"
                      value=""
                      displayEmpty
                      notched
                      onChange={(e) => cambiar("avatarModelUrl")(`${backendUrl}${e.target.value}`)}
                    >
                      <MenuItem value="" disabled>
                        <em>Elegir de la lista…</em>
                      </MenuItem>
                      {modelosDisponibles.map((m) => (
                        <MenuItem key={m.url} value={m.url}>
                          {m.nombre} ({m.tamanoMB}MB)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  label="URL del modelo (.glb)"
                  size="small"
                  fullWidth
                  placeholder="https://... (o elegilo de la lista de arriba)"
                  value={config.avatarModelUrl || ""}
                  onChange={(e) => cambiar("avatarModelUrl")(e.target.value)}
                  helperText="Tiene que ser compatible con TalkingHead.js (esqueleto Mixamo + blend shapes ARKit/Oculus)."
                />

                <Grupo classes={classes}>Apariencia y voz</Grupo>

                <FormControl size="small" fullWidth>
                  <InputLabel>Cuerpo del modelo</InputLabel>
                  <Select
                    label="Cuerpo del modelo"
                    value={config.avatarBody || "F"}
                    onChange={(e) => {
                      const nuevoBody = e.target.value;
                      cambiar("avatarBody")(nuevoBody);
                      const vozValida = REALTIME_VOICES.some((v) => v.value === realtimeVoice && v.gender === nuevoBody);
                      if (!vozValida) setRealtimeVoice("");
                    }}
                  >
                    <MenuItem value="F">Femenino</MenuItem>
                    <MenuItem value="M">Masculino</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth style={{ marginTop: 16 }}>
                  <InputLabel>Encuadre de cámara</InputLabel>
                  <Select
                    label="Encuadre de cámara"
                    value={config.avatarCameraView || "mid"}
                    onChange={(e) => cambiar("avatarCameraView")(e.target.value)}
                  >
                    {CAMERA_VIEWS.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography className={classes.ayuda}>
                  Cuánto del avatar se ve. Se aplica en la vista previa y en el widget.
                </Typography>

                {availableBackgrounds.length > 0 && (
                  <FormControl size="small" fullWidth style={{ marginTop: 16 }}>
                    <InputLabel shrink>Fondo disponible</InputLabel>
                    <Select
                      label="Fondo disponible"
                      value=""
                      displayEmpty
                      notched
                      onChange={(e) =>
                        cambiar("avatarBackgroundUrl")(
                          e.target.value ? `${backendUrl}${e.target.value}` : ""
                        )
                      }
                    >
                      <MenuItem value="">
                        <em>Sin fondo</em>
                      </MenuItem>
                      {availableBackgrounds.map((f) => (
                        <MenuItem key={f.url} value={f.url}>
                          {f.name} ({f.sizeMB}MB)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  label="URL del fondo (imagen)"
                  size="small"
                  fullWidth
                  style={{ marginTop: 16 }}
                  placeholder="https://... (o elegilo de la lista de arriba)"
                  value={config.avatarBackgroundUrl || ""}
                  onChange={(e) => cambiar("avatarBackgroundUrl")(e.target.value)}
                  helperText="Se dibuja detrás del avatar. Dejalo vacío para fondo transparente."
                />

                {promptId ? (
                  <FormControl size="small" fullWidth style={{ marginTop: 16 }}>
                    <InputLabel>Voz</InputLabel>
                    <Select
                      label="Voz"
                      value={realtimeVoice}
                      onChange={(e) => setRealtimeVoice(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Sin elegir</em>
                      </MenuItem>

                      {}
                      {REALTIME_VOICES.filter((v) => v.gender === (config.avatarBody || "F")).map((v) => (
                        <MenuItem key={v.value} value={v.value}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography className={classes.ayuda} style={{ marginTop: 16 }}>
                    Elegí un Prompt (IA) en Inteligencia para poder elegir la voz.
                  </Typography>
                )}

                {promptId && (
                  <Typography className={classes.ayuda}>
                    Filtradas por "Cuerpo del modelo" de arriba. Marin y Cedar tienen género
                    confirmado por OpenAI; las demás quedan clasificadas por cómo suenan.
                  </Typography>
                )}

                <FormControl size="small" fullWidth style={{ marginTop: 16 }}>
                  <InputLabel>Sensibilidad del micrófono</InputLabel>
                  <Select
                    label="Sensibilidad del micrófono"
                    value={config.micSensitivity || "low"}
                    onChange={(e) => cambiar("micSensitivity")(e.target.value)}
                  >
                    <MenuItem value="low">Baja (ambientes ruidosos)</MenuItem>
                    <MenuItem value="medium">Media</MenuItem>
                    <MenuItem value="high">Alta (ambientes silenciosos)</MenuItem>
                  </Select>
                </FormControl>
                <Typography className={classes.ayuda}>
                  Qué tan fuerte tiene que hablar el visitante para que el navegador detecte
                  su turno. Alta en un ambiente ruidoso hace que un ruido de fondo se tome
                  como si hubiera hablado; baja exige una voz más clara. No afecta al avatar
                  mientras habla: nunca se lo interrumpe.
                </Typography>

                <Grupo classes={classes}>Personalización de la burbuja (sitio web)</Grupo>

                <Typography className={classes.ayuda}>
                  Esto es para cuando se pega el &lt;script&gt; en un sitio de verdad -- en la
                  app el avatar ocupa toda la pantalla, así que ahí no hay burbuja ni posición
                  que elegir (el color y la forma sí se ven en los dos lados).
                </Typography>

                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <TextField
                    label="Color de marca"
                    type="color"
                    size="small"
                    value={config.color || "#3956ff"}
                    onChange={(e) => cambiar("color")(e.target.value)}
                    style={{ width: 96 }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Forma de la tarjeta</InputLabel>
                    <Select
                      label="Forma de la tarjeta"
                      value={config.shape || "rounded"}
                      onChange={(e) => cambiar("shape")(e.target.value)}
                    >
                      <MenuItem value="square">Cuadrada</MenuItem>
                      <MenuItem value="rounded">Redondeada</MenuItem>
                      <MenuItem value="veryRounded">Muy redondeada</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: "flex", gap: 2, marginTop: "16px" }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Posición</InputLabel>
                    <Select
                      label="Posición"
                      value={config.position || "right"}
                      onChange={(e) => cambiar("position")(e.target.value)}
                    >
                      <MenuItem value="right">Esquina derecha</MenuItem>
                      <MenuItem value="left">Esquina izquierda</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Tamaño</InputLabel>
                    <Select
                      label="Tamaño"
                      value={config.size || "small"}
                      onChange={(e) => cambiar("size")(e.target.value)}
                    >
                      <MenuItem value="small">Chica</MenuItem>
                      <MenuItem value="medium">Media</MenuItem>
                      <MenuItem value="large">Grande</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  label="Nombre del avatar"
                  size="small"
                  fullWidth
                  style={{ marginTop: 16 }}
                  value={config.avatarName ?? "Avatar"}
                  onChange={(e) => cambiar("avatarName")(e.target.value)}
                  helperText="Se muestra arriba de todo en la pantalla completa de la app."
                />

                <TextField
                  label="Texto del botón"
                  size="small"
                  fullWidth
                  style={{ marginTop: 16 }}
                  value={config.buttonText ?? "Comenzar"}
                  onChange={(e) => cambiar("buttonText")(e.target.value)}
                />

                <TextField
                  label="Aviso junto a la burbuja (opcional)"
                  size="small"
                  fullWidth
                  style={{ marginTop: 16 }}
                  placeholder="Vacío para no mostrar ningún aviso"
                  value={config.noticeText ?? ""}
                  onChange={(e) => cambiar("noticeText")(e.target.value)}
                  helperText="Aparece al lado de la burbuja antes de que la abran, como '¿Querés hablar con alguien?'."
                />

                <Typography className={classes.ayuda} style={{ marginTop: 16 }}>
                  Mirá "Vista previa" (en el menú de la izquierda) para ver cómo queda esto en
                  una web y en la app, uno al lado del otro.
                </Typography>
              </>
            )}

            {pestana === "vistaprevia" && (
              <>
                <Grupo classes={classes}>Cómo se ve</Grupo>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    justifyContent: "center",
                    alignItems: "flex-start",
                  }}
                >
                  {}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                    <Typography className={classes.previaRotulo}>En un sitio web</Typography>
                    <Box
                      sx={{
                        width: 380,
                        maxWidth: "100%",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 14px 34px rgba(0,0,0,.16)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          padding: "10px 14px",
                          bgcolor: (t) => (t.palette.mode === "light" ? "#f6f5f1" : "#1a1d24"),
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 0.6 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#e5988c" }} />
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#e3c98c" }} />
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#9bcf9b" }} />
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            fontSize: 10.5,
                            color: "text.secondary",
                            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "#111318"),
                            borderRadius: "20px",
                            padding: "4px 12px",
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: config.color || "#3956ff" }} />
                          clientedeejemplo.com
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          position: "relative",
                          height: 320,
                          bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "#15171c"),
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            padding: "13px 18px",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: "5px",
                              bgcolor: config.color || "#3956ff",
                            }}
                          />
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary" }}>
                            Ejemplo&nbsp;Co.
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1.75, marginLeft: "auto" }}>
                            {["Producto", "Precios", "Nosotros"].map((texto) => (
                              <Typography
                                key={texto}
                                sx={{ fontSize: 10, fontWeight: 500, color: "text.secondary" }}
                              >
                                {texto}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            position: "relative",
                            padding: "26px 20px 20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.1,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: -70,
                              right: -50,
                              width: 200,
                              height: 200,
                              borderRadius: "50%",
                              background: `radial-gradient(circle, ${mezclarConBlanco(
                                config.color || "#3956ff",
                                0.55
                              )} 0%, transparent 72%)`,
                              pointerEvents: "none",
                            }}
                          />
                          <Box
                            sx={{
                              position: "relative",
                              alignSelf: "flex-start",
                              fontSize: 9.5,
                              fontWeight: 700,
                              letterSpacing: 0.4,
                              textTransform: "uppercase",
                              color: config.color || "#3956ff",
                              bgcolor: (t) =>
                                mezclarConBlanco(config.color || "#3956ff", t.palette.mode === "light" ? 0.88 : 0.75),
                              padding: "3px 9px",
                              borderRadius: "20px",
                            }}
                          >
                            Nuevo
                          </Box>
                          <Typography
                            sx={{
                              position: "relative",
                              fontSize: 18,
                              fontWeight: 800,
                              color: "text.primary",
                              lineHeight: 1.25,
                              maxWidth: 230,
                            }}
                          >
                            Todo tu negocio, en un solo lugar
                          </Typography>
                          <Typography
                            sx={{
                              position: "relative",
                              fontSize: 11,
                              color: "text.secondary",
                              lineHeight: 1.5,
                              maxWidth: 220,
                            }}
                          >
                            Automatiza tareas, atiende clientes y crece sin complicarte.
                          </Typography>
                          <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1.5, marginTop: 0.5 }}>
                            <Box
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#fff",
                                bgcolor: config.color || "#3956ff",
                                padding: "7px 14px",
                                borderRadius: "7px",
                              }}
                            >
                              Empezar gratis
                            </Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.primary" }}>
                              Ver demo →
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 16,
                            [config.position === "left" ? "left" : "right"]: 16,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: config.position === "left" ? "flex-start" : "flex-end",
                          }}
                        >
                          {config.noticeText && (
                            <Box
                              sx={{
                                position: "relative",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                                bgcolor: "background.paper",
                                color: "text.primary",
                                padding: "10px 28px 10px 12px",
                                borderRadius: "14px",
                                marginBottom: "20px",
                                maxWidth: 200,
                                boxShadow: "0 10px 24px rgba(0,0,0,.16)",
                                border: "1px solid",
                                borderColor: "divider",
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  bottom: -6,
                                  [config.position === "left" ? "left" : "right"]: 24,
                                  width: 12,
                                  height: 12,
                                  bgcolor: "background.paper",
                                  borderRight: "1px solid",
                                  borderBottom: "1px solid",
                                  borderColor: "divider",
                                  transform: "rotate(45deg)",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  flexShrink: 0,
                                  width: 26,
                                  height: 26,
                                  borderRadius: "50%",
                                  background: `radial-gradient(circle at 50% 35%, ${mezclarConBlanco(
                                    config.color || "#3956ff",
                                    0.85
                                  )} 0%, ${mezclarConBlanco(config.color || "#3956ff", 0.6)} 100%)`,
                                }}
                              />
                              <Typography sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.4 }}>
                                {config.noticeText}
                              </Typography>
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  right: 8,
                                  fontSize: 13,
                                  color: "text.secondary",
                                  opacity: 0.45,
                                  lineHeight: 1,
                                }}
                              >
                                ×
                              </Box>
                            </Box>
                          )}
                          <Box
                            sx={{
                              position: "relative",
                              overflow: "hidden",
                              width: TAMANO_PX[config.size || "small"].w,
                              height: TAMANO_PX[config.size || "small"].h,
                              borderRadius: FORMA_RADIOS[config.shape || "rounded"].burbuja + "px",
                              background: `radial-gradient(circle at 50% 35%, ${mezclarConBlanco(config.color || "#3956ff", 0.95)} 0%, ${mezclarConBlanco(config.color || "#3956ff", 0.85)} 55%, ${mezclarConBlanco(config.color || "#3956ff", 0.72)} 100%)`,
                              boxShadow: "0 10px 26px rgba(0,0,0,.28)",
                            }}
                          >
                            <Avatar3DCanvas url={config.avatarModelUrl} body={config.avatarBody} cameraView={config.avatarCameraView} background={config.avatarBackgroundUrl} classes={classes} />
                            {}
                            <Box
                              sx={{
                                position: "absolute",
                                top: 10,
                                [config.position === "left" ? "left" : "right"]: 10,
                                width: 11,
                                height: 11,
                                borderRadius: "50%",
                                bgcolor: "#22c55e",
                                border: "2px solid #fff",
                              }}
                            />
                          </Box>
                          <Button
                            size="small"
                            style={{
                              background: config.color || "#3956ff",
                              color: "#fff",
                              fontWeight: 600,
                              textTransform: "none",
                              whiteSpace: "nowrap",
                              marginTop: -16,
                              zIndex: 1,
                              boxShadow: "0 6px 16px rgba(0,0,0,.3)",
                              borderRadius: Math.round(FORMA_RADIOS[config.shape || "rounded"].burbuja * 0.7),
                            }}
                          >
                            {config.buttonText || "Comenzar"}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                    <Typography className={classes.ayuda} style={{ textAlign: "center", maxWidth: 340 }}>
                      Así se ve al pegar el &lt;script&gt; en un sitio de verdad.
                    </Typography>
                  </Box>

                  {}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                    <Typography className={classes.previaRotulo}>En la app</Typography>
                    <Box
                      sx={{
                        position: "relative",
                        width: 210,
                        border: "11px solid",
                        borderColor: (t) => (t.palette.mode === "light" ? "#1c1f26" : "#000"),
                        borderRadius: "40px",
                        background: (t) => (t.palette.mode === "light" ? "#1c1f26" : "#000"),
                        boxShadow: "0 20px 46px rgba(0,0,0,.32)",
                      }}
                    >
                      {}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 70,
                          height: 18,
                          borderRadius: "10px",
                          background: (t) => (t.palette.mode === "light" ? "#1c1f26" : "#000"),
                          zIndex: 2,
                        }}
                      />
                      <Box
                        sx={{
                          position: "relative",
                          height: 400,
                          borderRadius: "30px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background: `radial-gradient(circle at 50% 40%, ${mezclarConBlanco(config.color || "#3956ff", 0.9)} 0%, ${mezclarConBlanco(config.color || "#3956ff", 0.6)} 60%, ${mezclarConBlanco(config.color || "#3956ff", 0.3)} 100%)`,
                          }}
                        >
                          <Avatar3DCanvas url={config.avatarModelUrl} body={config.avatarBody} cameraView={config.avatarCameraView} background={config.avatarBackgroundUrl} classes={classes} />
                        </Box>

                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 34,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "0 18px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#fff",
                            textShadow: "0 1px 4px rgba(0,0,0,.5)",
                            zIndex: 2,
                          }}
                        >
                          <span>9:41</span>
                          <Box sx={{ width: 15, height: 8, borderRadius: "2px", border: "1px solid #fff", opacity: 0.9 }} />
                        </Box>

                        <Box sx={{ position: "absolute", top: 46, left: 0, right: 0, textAlign: "center", zIndex: 2 }}>
                          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13.5, textShadow: "0 1px 5px rgba(0,0,0,.5)" }}>
                            {config.avatarName || "Avatar"}
                          </Typography>
                          <Typography sx={{ color: "rgba(255,255,255,.9)", fontSize: 10.5, textShadow: "0 1px 5px rgba(0,0,0,.5)" }}>
                            Listo para hablar
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "linear-gradient(to top, rgba(0,0,0,.68) 0%, rgba(0,0,0,.35) 55%, transparent 100%)",
                            padding: "48px 18px 18px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1.25,
                            zIndex: 2,
                          }}
                        >
                          <Button
                            size="small"
                            style={{
                              background: config.color || "#3956ff",
                              color: "#fff",
                              fontWeight: 600,
                              textTransform: "none",
                              whiteSpace: "nowrap",
                              padding: "9px 30px",
                              borderRadius: 24,
                              boxShadow: "0 8px 20px rgba(0,0,0,.35)",
                            }}
                          >
                            {config.buttonText || "Comenzar"}
                          </Button>
                          <Box sx={{ width: 70, height: 4, borderRadius: "2px", bgcolor: "#fff", opacity: 0.6 }} />
                        </Box>
                      </Box>
                    </Box>
                    <Typography className={classes.ayuda} style={{ textAlign: "center", maxWidth: 220 }}>
                      Acá ocupa toda la pantalla -- no hay esquina ni tamaño que elegir.
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {pestana === "enrutamiento" && (
              <>
                <Grupo classes={classes}>Enrutamiento</Grupo>

                <QueueSelect selectedQueueIds={colas} onChange={(valores) => setColas(valores)} />
                <Typography className={classes.ayuda}>
                  Solo organiza los tickets que genere el avatar en el panel; no afecta
                  qué tan inteligente es.
                </Typography>
              </>
            )}

            {pestana === "integracion" && (
              <>
                <Grupo classes={classes}>Seguridad</Grupo>

                <Box>
                  <Box display="flex" style={{ gap: 8 }}>
                    <TextField
                      label="Dominios permitidos"
                      size="small"
                      fullWidth
                      placeholder="https://mi-sitio-web.com"
                      value={origenNuevo}
                      onChange={(e) => setOrigenNuevo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          agregarOrigen();
                        }
                      }}
                    />
                    <Button size="small" onClick={agregarOrigen}>
                      Añadir
                    </Button>
                  </Box>

                  <Box className={classes.chips}>
                    {(config.allowedOrigins || []).map((d) => (
                      <Chip
                        key={d}
                        label={d}
                        size="small"
                        onDelete={() =>
                          setConfig((p) => ({
                            ...p,
                            allowedOrigins: p.allowedOrigins.filter((x) => x !== d),
                          }))
                        }
                      />
                    ))}
                  </Box>

                  <Typography className={classes.ayuda}>
                    {(config.allowedOrigins || []).length === 0
                      ? "Sin dominios, el avatar se acepta desde cualquier web. Cualquiera que copie tu token podría usarlo."
                      : "Solo estos sitios podrán abrir el avatar con tu token."}
                  </Typography>
                </Box>

                <Grupo classes={classes}>Herramientas</Grupo>

                <Box>
                  {TOOLS_DISPONIBLES.map((t) => (
                    <Ajuste
                      key={t.id}
                      classes={classes}
                      titulo={t.etiqueta}
                      descripcion={t.descripcion}
                      control={
                        <Switch
                          checked={(config.toolIds || []).includes(t.id)}
                          onChange={() => alternarTool(t.id)}
                        />
                      }
                    />
                  ))}
                </Box>

                <Grupo classes={classes}>Identificación</Grupo>

                <Ajuste
                  classes={classes}
                  titulo="Pedir nombre y teléfono"
                  descripcion={
                    config.askForDetails === "before"
                      ? "Se piden antes de poder llamar, con un formulario. Cuando haga falta un asesor, el avatar ya los tiene y no los vuelve a preguntar."
                      : "No se piden antes de llamar. Si hace falta un asesor, el avatar los pide en ese momento, por voz."
                  }
                  control={
                    <TextField
                      size="small"
                      select
                      value={config.askForDetails || "never"}
                      onChange={(e) => cambiar("askForDetails")(e.target.value)}
                      style={{ width: 190 }}
                    >
                      <MenuItem value="never">No pedir</MenuItem>
                      <MenuItem value="before">Antes de llamar</MenuItem>
                    </TextField>
                  }
                />

                <Grupo classes={classes}>Probar</Grupo>

                <Box>
                  <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                    <Typography style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                      Página de prueba
                    </Typography>
                    <Button size="small" color="inherit" onClick={() => copiar(pruebaUrl)}>
                      Copiar link
                    </Button>
                    <Button size="small" onClick={() => window.open(pruebaUrl, "_blank", "noopener")}>
                      Abrir
                    </Button>
                  </Box>

                  <Typography className={classes.ayuda}>
                    Habla directo con el avatar sin instalar nada en ningún sitio -- ideal
                    para probar mientras configurás. Funciona aunque el avatar todavía no
                    esté habilitado en ningún dominio (esta página no chequea "Dominios
                    permitidos" de arriba, solo que el avatar esté habilitado en General).
                  </Typography>
                </Box>

                <Grupo classes={classes}>App móvil</Grupo>

                <Ajuste
                  classes={classes}
                  titulo="Usar en la app"
                  descripcion="Marcalo para que la app (React Native) use esta conexión sin tener la URL quemada en su código -- consulta /avatar/app-config y recibe la URL de abajo."
                  control={
                    <Switch
                      checked={config.exposedToApp === true}
                      onChange={(e) => cambiar("exposedToApp")(e.target.checked)}
                    />
                  }
                />

                {config.exposedToApp && (
                  <Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                      <Typography style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                        URL asignada a la app
                      </Typography>
                      <Button size="small" color="inherit" onClick={() => copiar(pruebaUrl)}>
                        Copiar link
                      </Button>
                    </Box>

                    <Typography className={classes.ayuda}>
                      Es la misma URL de "Probar" de arriba -- es fija mientras no toques "Cambiar
                      token" en Instalación, así que se puede compartir/copiar tranquilo.
                    </Typography>
                  </Box>
                )}

                <Grupo classes={classes}>Instalación</Grupo>

                <Box>
                  <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                    <Typography style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                      Snippet de código
                    </Typography>
                    <Button size="small" color="inherit" onClick={() => copiar(snippet)}>
                      Copiar
                    </Button>
                    <Button size="small" color="warning" onClick={rotarToken}>
                      Cambiar token
                    </Button>
                  </Box>

                  <Box className={classes.codigo}>{snippet}</Box>

                  <Typography className={classes.ayuda}>
                    Pégalo antes de <code>&lt;/body&gt;</code> en el sitio del cliente.
                  </Typography>
                </Box>
              </>
            )}

            {pestana === "consumo" && (
              <>
                <Grupo classes={classes}>Este mes</Grupo>

                {cargandoUso && <Typography className={classes.ayuda}>Cargando…</Typography>}

                {!cargandoUso && uso && (
                  <>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                      {[
                        { rotulo: "Llamadas", valor: uso.calls },
                        { rotulo: "Tiempo total", valor: formatearMinutos(uso.totalSeconds) },
                        { rotulo: "Habló el avatar", valor: formatearMinutos(uso.spokenSeconds) },
                        { rotulo: "Promedio por llamada", valor: formatearMinutos(uso.averageSeconds) },
                      ].map((d) => (
                        <Box
                          key={d.rotulo}
                          sx={{
                            flex: "1 1 150px",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "10px",
                            padding: "12px 14px",
                          }}
                        >
                          <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>
                            {d.rotulo}
                          </Typography>
                          <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>
                            {d.valor}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {config.monthlyMinutesLimit > 0 && (
                      <>
                        <Grupo classes={classes}>Tope mensual</Grupo>

                        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 0.75 }}>
                          <span>
                            {Math.round(uso.totalSeconds / 60)} de {config.monthlyMinutesLimit} min
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            {Math.min(100, Math.round((uso.totalSeconds / 60 / config.monthlyMinutesLimit) * 100))}%
                          </span>
                        </Box>

                        <Box sx={{ height: 8, borderRadius: "4px", bgcolor: "action.hover", overflow: "hidden" }}>
                          <Box
                            sx={{
                              height: "100%",
                              width: `${Math.min(100, (uso.totalSeconds / 60 / config.monthlyMinutesLimit) * 100)}%`,
                              bgcolor: uso.totalSeconds / 60 >= config.monthlyMinutesLimit ? "error.main" : "success.main",
                            }}
                          />
                        </Box>

                        {uso.totalSeconds / 60 >= config.monthlyMinutesLimit && (
                          <Typography className={classes.ayuda} style={{ marginTop: 8 }}>
                            El tope está alcanzado: el avatar no acepta llamadas nuevas hasta el mes que viene.
                          </Typography>
                        )}
                      </>
                    )}

                    <Grupo classes={classes}>Cómo terminaron</Grupo>

                    {uso.byOutcome.length === 0 && (
                      <Typography className={classes.ayuda}>
                        Todavía no hay llamadas registradas este mes.
                      </Typography>
                    )}

                    {uso.byOutcome.map((r) => (
                      <Box
                        key={r.outcome}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "7px 0",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          fontSize: 13,
                        }}
                      >
                        <span>{ETIQUETA_RESULTADO[r.outcome] || r.outcome}</span>
                        <span style={{ fontWeight: 600 }}>
                          {r.calls} · {formatearMinutos(r.seconds)}
                        </span>
                      </Box>
                    ))}

                    <Grupo classes={classes}>Costo estimado</Grupo>

                    <Typography sx={{ fontSize: 26, fontWeight: 700 }}>
                      US$ {uso.estimatedCostUsd.toFixed(2)}
                    </Typography>

                    <Typography className={classes.ayuda}>
                      Calculado con el precio de <code>{uso.model}</code>: la entrada sobre el
                      tiempo total (el micrófono se transmite toda la llamada) y la salida sobre
                      los segundos que el avatar habló de verdad, medidos en cada llamada.
                      {uso.callsWithoutMeasurement > 0 && (
                        <> Ojo: {uso.callsWithoutMeasurement} llamada(s) de este mes son anteriores
                        a esta medición y se estiman asumiendo que habló la mitad del tiempo.</>
                      )}{" "}
                      Sigue siendo una aproximación cercana, no tu factura: el número oficial
                      está en el panel de facturación de OpenAI.
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>

          {pestana !== "integracion" && pestana !== "vistaprevia" && pestana !== "consumo" && (
            <Avatar3DPreview
              url={config.avatarModelUrl}
              body={config.avatarBody}
              cameraView={config.avatarCameraView}
              background={config.avatarBackgroundUrl}
              color={config.color}
              classes={classes}
            />
          )}
        </Box>

        <Box className={classes.pie}>
          <Typography className={classes.ayuda}>
            Los cambios se aplican sin volver a tocar el script.
          </Typography>

          <Box display="flex" style={{ gap: 8 }}>
            <Button size="small" color="inherit" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={guardando || !nombre.trim()}
              onClick={guardar}
            >
              {guardando ? "Guardando…" : "Guardar"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default AvatarModal;
